import { useState } from 'react';
import { User, Tenant } from '../types/user';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, User as UserIcon, Mail, Building2, Calendar, Crown, LogOut } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useMobile } from '../hooks/use-mobile';
import { toast } from 'sonner';

interface UserProfileProps {
  user: User;
  tenant: Tenant;
  onClose: () => void;
  onLogout: () => void;
  onUpdate: (user: User) => void;
}

export function UserProfile({ user, tenant, onClose, onLogout, onUpdate }: UserProfileProps) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMobile();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Atualizar na tabela users
      const { error: updateError } = await supabase
        .from('users')
        .update({
          name,
          email,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Atualizar email no Supabase Auth
      const { error: authError } = await supabase.auth.updateUser({
        email,
        data: { name }
      });

      if (authError) throw authError;

      // Atualizar estado local
      const updatedUser = { ...user, name, email };
      onUpdate(updatedUser);
      
      toast.success('Perfil atualizado com sucesso!');
    } catch (err: any) {
      console.error('Erro ao atualizar perfil:', err);
      setError(err.message || 'Erro ao salvar alterações.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(date));
  };

  return (
    <div className={`${isMobile ? 'fixed inset-0 bg-gray-50' : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center'} z-[100] ${isMobile ? '' : 'p-4'}`}>
      <div className={`bg-white ${isMobile ? 'h-full w-full' : 'rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh]'} overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-600 to-purple-600">
          <div>
            <h2 className="text-xl font-bold text-white">Meu Perfil</h2>
            <p className="text-blue-100 text-sm mt-1">Gerencie suas informações pessoais</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Avatar Section */}
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">{user.name}</h3>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Empresa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold text-gray-900">{tenant.name}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Plano
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold text-gray-900 capitalize">
                  {tenant.plan === 'free' ? 'Gratuito' : 'Premium'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <UserIcon className="w-4 h-4" />
                  Tipo de Conta
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold text-gray-900">
                  {user.role === 'owner' ? 'Proprietário' : user.role === 'manager' ? 'Gerente' : 'Usuário'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Membro desde
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(user.createdAt)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Edit Form */}
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                Nome completo
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="mt-1"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full"
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </form>
        </div>

        {/* Footer com botão Sair - fixo no mobile */}
        {isMobile && (
          <div className="flex-shrink-0 p-4 border-t bg-white">
            <Button
              type="button"
              variant="outline"
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 text-red-600 border-red-600 hover:bg-red-50 py-3"
            >
              <LogOut className="w-5 h-5" />
              Sair da conta
            </Button>
          </div>
        )}

        {/* Botão Sair no desktop (inline com salvar) */}
        {!isMobile && (
          <div className="flex-shrink-0 p-6 border-t bg-gray-50">
            <Button
              type="button"
              variant="outline"
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 text-red-600 border-red-600 hover:bg-red-50 py-3"
            >
              <LogOut className="w-5 h-5" />
              Sair da conta
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

